import React, { useState, useEffect } from 'react';
import { 
    FaBook, FaCalendarAlt, FaGraduationCap, FaUserTie, FaCog, FaList,
    FaArrowRight, FaArrowLeft, FaSave, FaSpinner, FaTimes, FaForward,
    FaPlus, FaMinus, FaCheckCircle, FaUsers, FaClipboardList, FaInfoCircle
} from 'react-icons/fa';
import { AiOutlineClose } from 'react-icons/ai';
import CurriculumFormManager from './CurriculumFormManager';
import LoadingSpinner from './LoadingSpinner';
import { normalizeUsers } from '../utils/userUtils';
import { getApiUrl } from '../utils/config';
import { buildCurriculumDateDefaults, splitDateRangeIntoRotations, getRotationRangeFromRotations } from '../utils/dateUtils';
import {
    MATCH_BY_INSTITUTION,
    buildCurriculumDefaultsFromRank,
    findRank,
    getMatchByMeta,
    normalizeMatchBy,
    preceptorServesRank
} from '../utils/rankUtils';
import '../styles/ManageCurriculums.css';

const CreateCurriculumModern = ({
    isOpen,
    onClose,
    onSuccess,
    embedded = false,
    onCancel,
    onNotifySuccess,
    onNotifyError
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [students, setStudents] = useState([]);
    const [preceptors, setPreceptors] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [preceptorsLoading, setPreceptorsLoading] = useState(false);

    // State for specialities and institutions
    const [specialities, setSpecialities] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [specialitiesLoading, setSpecialitiesLoading] = useState(false);
    const [institutionsLoading, setInstitutionsLoading] = useState(false);

    // Ranks come from the database (Manage Ranks) rather than being hardcoded.
    const [ranks, setRanks] = useState([]);
    const [ranksLoading, setRanksLoading] = useState(false);

    // State for each step
    const getDefaultState = (rotationCount = 6) => {
        const defaults = buildCurriculumDateDefaults(rotationCount);
        return {
            basicData: {
                name: '',
                description: '',
                start_date: defaults.curriculumStartDate,
                end_date: defaults.curriculumEndDate,
                rankId: '',
                student_ids: [],
                preceptor_ids: []
            },
            configData: {
                rotationsCount: rotationCount,
                rotations: defaults.rotations,
                form_registration: defaults.formRegistration,
                choicesCount: 4,
                preceptor_to_student_eval: [],
                student_to_preceptor_eval: [],
                spep_prefs: false,
                sitePreceptorOnly: false,
                defaultFullTimeHrs: { start_time: '08:00', end_time: '16:00' },
                defaultPartTimeHrs: { start_time: '08:00', end_time: '12:00' }
            }
        };
    };

    const initialState = getDefaultState();
    const [basicData, setBasicData] = useState(initialState.basicData);
    
    const [configData, setConfigData] = useState(initialState.configData);

    // Constraints are a single list; whether their entities are experiences or
    // institutions comes from the selected rank's matchBy.
    const [constraintsData, setConstraintsData] = useState({ constraints: [] });

    // Step 2: Forms data
    const [formsData, setFormsData] = useState([]);
    const stageMeta = [
        { id: 1, title: 'Basic Info' },
        { id: 2, title: 'Config' },
        { id: 3, title: 'Constraints' },
        { id: 4, title: 'Forms' }
    ];
    const notifyError = (message) => {
        if (onNotifyError) {
            onNotifyError(message);
        } else if (!embedded) {
            alert(message);
        }
    };

    // Helper function to create a new constraint. Its entities are experiences or
    // institutions depending on the selected rank's matchBy.
    const createNewConstraint = () => ({
        id: Date.now(),
        minimumNumber: 1,
        fromRotation: 1,
        toRotation: configData.rotationsCount,
        entities: []
    });

    const updateConstraint = (index, field, value) => {
        setConstraintsData((prev) => {
            const constraints = [...prev.constraints];
            constraints[index] = { ...constraints[index], [field]: value };
            return { constraints };
        });
    };

    // Shared styling for the constraint inputs.
    const constraintFieldStyle = {
        width: '100%',
        padding: '0.875rem',
        border: '2px solid #e5e7eb',
        borderRadius: '12px',
        fontSize: '0.9rem',
        outline: 'none',
        background: '#f9fafb',
        boxSizing: 'border-box',
        transition: 'all 0.2s ease'
    };

    const handleConstraintFieldFocus = (e) => {
        e.target.style.borderColor = '#3b82f6';
        e.target.style.background = 'white';
        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
    };

    const handleConstraintFieldBlur = (e) => {
        e.target.style.borderColor = '#e5e7eb';
        e.target.style.background = '#f9fafb';
        e.target.style.boxShadow = 'none';
    };

    // Initialize rotations with near-future default dates
    const initializeRotations = (count) => {
        const defaults = buildCurriculumDateDefaults(count);
        setConfigData(prev => ({
            ...prev,
            rotationsCount: count,
            rotations: defaults.rotations,
            form_registration: defaults.formRegistration,
        }));
        setBasicData(prev => ({
            ...prev,
            start_date: prev.start_date || defaults.curriculumStartDate,
            end_date: prev.end_date || defaults.curriculumEndDate,
        }));
    };

    // The rank currently selected, and what its curricula match preceptors on.
    const selectedRank = findRank(ranks, basicData.rankId);
    const matchBy = normalizeMatchBy(selectedRank?.matchBy);
    const matchByMeta = getMatchByMeta(matchBy);
    // Constraint entities follow the rank's match type.
    const constraintEntities = matchBy === MATCH_BY_INSTITUTION ? institutions : specialities;

    /**
     * Apply a newly selected rank: load its rotation-count and rotation-date
     * defaults into the config step, and clear user/constraint selections that
     * were scoped to the previous rank.
     */
    const handleRankChange = (rankId) => {
        const rank = findRank(ranks, rankId);

        setBasicData((prev) => ({
            ...prev,
            rankId,
            // Eligibility depends on the rank, so previous picks no longer apply.
            student_ids: [],
            preceptor_ids: []
        }));

        // Constraint entities change type when the match type changes.
        setConstraintsData({ constraints: [] });

        if (!rank) return;

        const defaults = buildCurriculumDefaultsFromRank(rank);
        setConfigData((prev) => ({
            ...prev,
            rotationsCount: defaults.rotationsCount,
            // Fall back to evenly split dates when the rank has no date ranges set.
            rotations: defaults.hasDates
                ? defaults.rotations
                : buildCurriculumDateDefaults(defaults.rotationsCount).rotations
        }));

        if (defaults.hasDates) {
            setBasicData((prev) => ({
                ...prev,
                start_date: defaults.startDate,
                end_date: defaults.endDate
            }));
        }
    };

    // Effect to load data when component opens
    useEffect(() => {
        if (embedded || isOpen) {
            fetchStudents();
            fetchPreceptors();
            fetchSpecialities();
            fetchInstitutions();
            fetchRanks();
            initializeRotations(configData.rotationsCount);
        }
    }, [embedded, isOpen]);

    useEffect(() => {
        if (embedded) return undefined;
        document.body.classList.toggle('mc-modal-open', isOpen);
        return () => document.body.classList.remove('mc-modal-open');
    }, [embedded, isOpen]);

    // Effect to update rotations when count changes
    useEffect(() => {
        if (configData.rotationsCount > 0) {
            setConfigData(prev => {
                const rotationRange = getRotationRangeFromRotations(prev.rotations);
                if (!rotationRange) {
                    return {
                        ...prev,
                        rotations: buildCurriculumDateDefaults(prev.rotationsCount).rotations
                    };
                }

                return {
                    ...prev,
                    rotations: splitDateRangeIntoRotations(rotationRange.startDate, rotationRange.endDate, prev.rotationsCount)
                };
            });
        }
    }, [configData.rotationsCount]);

    const fetchStudents = async () => {
        setStudentsLoading(true);
        try {
            const response = await fetch(getApiUrl('crud/get_all_students_basic_info/'), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
            });
            if (response.ok) {
                const data = await response.json();
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Students API response:', data);
                let studentsList = data;
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    studentsList = data.data || data.students || [];
                }
                setStudents(normalizeUsers(Array.isArray(studentsList) ? studentsList : []));
            } else {
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('Failed to fetch students:', response.status);
            }
        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setStudentsLoading(false);
        }
    };

    const fetchPreceptors = async () => {
        setPreceptorsLoading(true);
        try {
            const response = await fetch(getApiUrl('crud/get_all_preceptors_basic_info/'), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
            });
            if (response.ok) {
                const data = await response.json();
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Preceptors API response:', data);
                let preceptorsList = data;
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    preceptorsList = data.data || data.preceptors || [];
                }
                setPreceptors(normalizeUsers(Array.isArray(preceptorsList) ? preceptorsList : []));
            } else {
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('Failed to fetch preceptors:', response.status);
            }
        } catch (error) {
            console.error('Error fetching preceptors:', error);
        } finally {
            setPreceptorsLoading(false);
        }
    };

    const fetchRanks = async () => {
        setRanksLoading(true);
        try {
            const response = await fetch(getApiUrl('ranks/ranks'), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
            });
            if (response.ok) {
                const data = await response.json();
                setRanks(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching ranks:', error);
        } finally {
            setRanksLoading(false);
        }
    };

    const fetchSpecialities = async () => {
        setSpecialitiesLoading(true);
        try {
            const response = await fetch(getApiUrl('crud/get_full_specialities'), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (response.ok) {
                const data = await response.json();
                setSpecialities(data);
            }
        } catch (error) {
            console.error('Error fetching specialities:', error);
        } finally {
            setSpecialitiesLoading(false);
        }
    };

    const fetchInstitutions = async () => {
        setInstitutionsLoading(true);
        try {
            const response = await fetch(getApiUrl('crud/get_full_institutions'), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (response.ok) {
                const data = await response.json();
                setInstitutions(data);
            }
        } catch (error) {
            console.error('Error fetching institutions:', error);
        } finally {
            setInstitutionsLoading(false);
        }
    };

    // Helper function to group items by their tags
    const groupItemsByTags = (items) => {
        const grouped = {};
        const ungrouped = [];

        items.forEach(item => {
            if (item.groups && item.groups.length > 0) {
                item.groups.forEach(group => {
                    if (!grouped[group]) {
                        grouped[group] = [];
                    }
                    grouped[group].push(item);
                });
            } else {
                ungrouped.push(item);
            }
        });

        return { grouped, ungrouped };
    };

    // Modern multi-select dropdown component
    const ModernMultiSelectDropdown = ({ 
        items, 
        selectedItems, 
        onSelectionChange, 
        placeholder, 
        loading, 
        label 
    }) => {
        const [isOpen, setIsOpen] = useState(false);
        const dropdownRef = React.useRef(null);
        const { grouped, ungrouped } = groupItemsByTags(items);

        // Close dropdown when clicking outside
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setIsOpen(false);
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, []);

        const handleItemToggle = (item) => {
            const isSelected = selectedItems.some(selected => selected._id === item._id);
            if (isSelected) {
                onSelectionChange(selectedItems.filter(selected => selected._id !== item._id));
            } else {
                onSelectionChange([...selectedItems, item]);
            }
            // Keep dropdown open for multiple selections
        };

        const handleGroupToggle = (groupName, groupItems) => {
            const groupItemIds = groupItems.map(item => item._id);
            const allGroupItemsSelected = groupItemIds.every(id => 
                selectedItems.some(selected => selected._id === id)
            );

            if (allGroupItemsSelected) {
                // Remove all group items
                onSelectionChange(selectedItems.filter(selected => 
                    !groupItemIds.includes(selected._id)
                ));
            } else {
                // Add all group items that aren't already selected
                const newItems = groupItems.filter(item => 
                    !selectedItems.some(selected => selected._id === item._id)
                );
                onSelectionChange([...selectedItems, ...newItems]);
            }
            // Keep dropdown open for multiple selections
        };

        const removeItem = (itemToRemove) => {
            onSelectionChange(selectedItems.filter(item => item._id !== itemToRemove._id));
        };

        const isGroupFullySelected = (groupItems) => {
            return groupItems.every(item => 
                selectedItems.some(selected => selected._id === item._id)
            );
        };

        const isGroupPartiallySelected = (groupItems) => {
            return groupItems.some(item => 
                selectedItems.some(selected => selected._id === item._id)
            ) && !isGroupFullySelected(groupItems);
        };

        return (
            <div style={{ marginBottom: '1.5rem' }} ref={dropdownRef}>
                <label style={{ 
                    display: 'block', 
                    marginBottom: '0.75rem', 
                    fontWeight: 600, 
                    color: '#374151',
                    fontSize: '0.9rem'
                }}>
                    {label}
                </label>
                
                {/* Selected items display */}
                {selectedItems.length > 0 && (
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        marginBottom: '0.75rem'
                    }}>
                        {selectedItems.map(item => (
                            <span
                                key={item._id}
                                style={{
                                    background: '#3b82f6',
                                    color: 'white',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '12px',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem'
                                }}
                            >
                                {item.name || item.speciality}
                                <button
                                    onClick={() => removeItem(item)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        padding: '0',
                                        marginLeft: '0.25rem',
                                        opacity: 0.8,
                                        transition: 'opacity 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.opacity = '1'}
                                    onMouseLeave={(e) => e.target.style.opacity = '0.8'}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {/* Dropdown */}
                <div style={{ position: 'relative' }}>
                    <button
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        disabled={loading}
                        style={{
                            width: '100%',
                            padding: '0.875rem',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            background: '#f9fafb',
                            boxSizing: 'border-box',
                            transition: 'all 0.2s ease',
                            textAlign: 'left',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: loading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <span style={{ color: selectedItems.length > 0 ? '#374151' : '#9ca3af' }}>
                            {loading ? 'Loading...' : (selectedItems.length > 0 ? `${selectedItems.length} selected` : placeholder)}
                        </span>
                        <span style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                            ▼
                        </span>
                    </button>

                    {isOpen && !loading && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            background: 'white',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            marginTop: '0.25rem',
                            maxHeight: '400px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                        }}>
                            {/* Groups Section */}
                            {Object.keys(grouped).length > 0 && (
                                <div style={{ padding: '0.5rem 0' }}>
                                    <div style={{
                                        padding: '0.5rem 1rem',
                                        background: '#1f2937',
                                        fontWeight: 700,
                                        fontSize: '0.85rem',
                                        color: 'white',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}>
                                        Groups
                                    </div>
                                    {Object.entries(grouped).map(([groupName, groupItems]) => {
                                        const fullySelected = isGroupFullySelected(groupItems);
                                        const partiallySelected = isGroupPartiallySelected(groupItems);
                                        
                                        return (
                                            <div
                                                key={`group-${groupName}`}
                                                onClick={() => handleGroupToggle(groupName, groupItems)}
                                                style={{
                                                    padding: '0.75rem 1.5rem',
                                                    cursor: 'pointer',
                                                    background: fullySelected ? '#dbeafe' : partiallySelected ? '#f0f9ff' : 'transparent',
                                                    borderLeft: fullySelected ? '4px solid #3b82f6' : partiallySelected ? '4px solid #3b82f6' : '4px solid transparent',
                                                    transition: 'all 0.2s ease',
                                                    fontSize: '0.9rem',
                                                    fontWeight: 600,
                                                    color: '#374151'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!fullySelected && !partiallySelected) {
                                                        e.target.style.background = '#f3f4f6';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!fullySelected && !partiallySelected) {
                                                        e.target.style.background = 'transparent';
                                                    }
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                    <div style={{
                                                        width: '18px',
                                                        height: '18px',
                                                        border: '2px solid #3b82f6',
                                                        borderRadius: '4px',
                                                        background: fullySelected ? '#3b82f6' : partiallySelected ? '#3b82f6' : 'white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '12px',
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {fullySelected ? '✓' : partiallySelected ? '-' : ''}
                                                    </div>
                                                    <div>
                                                        <div>{groupName}</div>
                                                        <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 400 }}>
                                                            {groupItems.length} {label.toLowerCase()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Individual Items Section */}
                            <div style={{ padding: '0.5rem 0' }}>
                                <div style={{
                                    padding: '0.5rem 1rem',
                                    background: '#374151',
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    color: 'white',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>
                                    Individual {label}
                                </div>
                                
                                {/* Grouped items - individual selection */}
                                {Object.entries(grouped).map(([groupName, groupItems]) => (
                                    <div key={`individual-${groupName}`} style={{ marginBottom: '0.5rem' }}>
                                        <div style={{
                                            padding: '0.375rem 1rem',
                                            background: '#f9fafb',
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>
                                            {groupName}
                                        </div>
                                        {groupItems.map(item => {
                                            const isSelected = selectedItems.some(selected => selected._id === item._id);
                                            return (
                                                <div
                                                    key={item._id}
                                                    onClick={() => handleItemToggle(item)}
                                                    style={{
                                                        padding: '0.6rem 1.75rem',
                                                        cursor: 'pointer',
                                                        background: isSelected ? '#eff6ff' : 'transparent',
                                                        borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                                                        transition: 'all 0.2s ease',
                                                        fontSize: '0.85rem',
                                                        color: '#374151'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isSelected) {
                                                            e.target.style.background = '#f9fafb';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!isSelected) {
                                                            e.target.style.background = 'transparent';
                                                        }
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => {}}
                                                            style={{ margin: 0, accentColor: '#3b82f6' }}
                                                        />
                                                        {item.name || item.speciality}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}

                                {/* Ungrouped items */}
                                {ungrouped.length > 0 && (
                                    <div>
                                        <div style={{
                                            padding: '0.375rem 1rem',
                                            background: '#f9fafb',
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>
                                            Other
                                        </div>
                                        {ungrouped.map(item => {
                                            const isSelected = selectedItems.some(selected => selected._id === item._id);
                                            return (
                                                <div
                                                    key={item._id}
                                                    onClick={() => handleItemToggle(item)}
                                                    style={{
                                                        padding: '0.6rem 1.75rem',
                                                        cursor: 'pointer',
                                                        background: isSelected ? '#eff6ff' : 'transparent',
                                                        borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                                                        transition: 'all 0.2s ease',
                                                        fontSize: '0.85rem',
                                                        color: '#374151'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isSelected) {
                                                            e.target.style.background = '#f9fafb';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!isSelected) {
                                                            e.target.style.background = 'transparent';
                                                        }
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => {}}
                                                            style={{ margin: 0, accentColor: '#3b82f6' }}
                                                        />
                                                        {item.name || item.speciality}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {items.length === 0 && (
                                <div style={{
                                    padding: '1rem',
                                    textAlign: 'center',
                                    color: '#9ca3af',
                                    fontSize: '0.875rem'
                                }}>
                                    No items available
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const handleNext = () => {
        if (currentStep === 1) {
            if (!basicData.name.trim()) {
                notifyError('Please enter a curriculum name');
                return;
            }
            if (!basicData.start_date) {
                notifyError('Please select a start date');
                return;
            }
            if (!basicData.end_date) {
                notifyError('Please select an end date');
                return;
            }
            if (new Date(basicData.start_date) >= new Date(basicData.end_date)) {
                notifyError('End date must be after start date');
                return;
            }
            if (!basicData.rankId) {
                notifyError('Please select a rank');
                return;
            }
        }
        
        if (currentStep < 4) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Backend expects datetime query params, while UI date inputs return YYYY-MM-DD.
    const toApiDateTime = (dateValue, { endOfDay = false } = {}) => {
        if (!dateValue) return '';
        if (typeof dateValue === 'string' && dateValue.includes('T')) return dateValue;

        const clock = endOfDay ? '23:59:59' : '00:00:00';
        const candidate = `${dateValue}T${clock}`;
        const parsed = new Date(candidate);
        return Number.isNaN(parsed.getTime()) ? dateValue : candidate;
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            
            // Step 1: Create the curriculum
            const curriculumParams = new URLSearchParams({
                name: basicData.name,
                start_date: toApiDateTime(basicData.start_date),
                end_date: toApiDateTime(basicData.end_date, { endOfDay: true }),
                rank_id: basicData.rankId
            });

            const requestBody = {
                forms: formsData.length > 0 ? formsData : null
            };

            if (import.meta.env.VITE_DEBUG_MODE === 'true') {
                console.log('Creating curriculum with params:', curriculumParams.toString());
                console.log('Creating curriculum with body:', requestBody);
            }
            const curriculumResponse = await fetch(getApiUrl(`curriculum/create_curriculum/?${curriculumParams}`), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!curriculumResponse.ok) {
                const errorData = await curriculumResponse.text();
                throw new Error(`Failed to create curriculum: ${errorData}`);
            }

            const createdCurriculum = await curriculumResponse.json();
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Curriculum created:', createdCurriculum);
            
            // Extract curriculum_id from response
            const curriculumId = createdCurriculum.curriculum_id || createdCurriculum._id || createdCurriculum.id;
            if (!curriculumId) {
                throw new Error('Curriculum ID not found in response');
            }

            // Step 2: Set configuration.
            // This MUST run before "Step 3: Add students and preceptors" below —
            // add_users initializes each new member's schedule sized to whatever
            // rotation count the curriculum's configuration holds at that moment.
            // create_curriculum only ever seeds a default (rank-based) rotation
            // count, so if this admin-chosen configuration were applied afterward
            // instead, every student/preceptor added during creation would end up
            // with a schedule sized to the wrong (default) rotation count.
            // Calculate year range from rotation dates
            const rotationDates = configData.rotations.map(rotation => ({
                start: new Date(rotation.startDate),
                end: new Date(rotation.endDate)
            }));
            
            const earliestStart = rotationDates.reduce((earliest, rotation) => 
                rotation.start < earliest ? rotation.start : earliest, 
                rotationDates[0]?.start || new Date()
            );
            
            const latestEnd = rotationDates.reduce((latest, rotation) => 
                rotation.end > latest ? rotation.end : latest, 
                rotationDates[0]?.end || new Date()
            );
            
            const startYear = earliestStart.getFullYear();
            const endYear = latestEnd.getFullYear();
            const yearRange = startYear === endYear ? `${startYear}` : `${startYear}/${endYear}`;
            
            const configurationData = {
                year: yearRange,
                rotations: configData.rotations.map(rotation => ({
                    startDate: rotation.startDate || '',
                    endDate: rotation.endDate || ''
                })).filter(r => r.startDate && r.endDate),
                form_registration: {
                    startDate: configData.form_registration?.startDate || '',
                    endDate: configData.form_registration?.endDate || ''
                },
                choicesNumber: configData.choicesCount || 4,
                spep_prefs: configData.spep_prefs || false,
                sitePreceptorOnly: configData.sitePreceptorOnly || false,
                defaultFullTimeHrs: {
                    start_time: configData.defaultFullTimeHrs?.start_time || "08:00",
                    end_time: configData.defaultFullTimeHrs?.end_time || "16:00"
                },
                defaultPartTimeHrs: {
                    start_time: configData.defaultPartTimeHrs?.start_time || "08:00", 
                    end_time: configData.defaultPartTimeHrs?.end_time || "12:00"
                },
                curriculum_id: curriculumId
            };
            
            // Ensure required rotation dates are present
            if (!configurationData.rotations || configurationData.rotations.length === 0) {
                throw new Error('No valid rotation dates provided. Please ensure all rotations have start and end dates.');
            }

            // Create query parameters for required fields
            const configParams = new URLSearchParams({
                rotationsNumber: configData.rotationsCount.toString(),
                choicesNumber: configData.choicesCount.toString(),
                curriculum_id: curriculumId
            });

            if (import.meta.env.VITE_DEBUG_MODE === 'true') {
                console.log('Setting configuration:', configurationData);
                console.log('Configuration params:', configParams.toString());
            }
            const configResponse = await fetch(getApiUrl(`config/set_configurations/?${configParams}`), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(configurationData)
            });

            if (!configResponse.ok) {
                const errorData = await configResponse.text();
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('Failed to set configuration:', errorData);
            } else {
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Configuration set successfully');
            }

            // Step 3: Add students and preceptors if any selected — runs after
            // configuration is set (see the note above Step 2) so their schedules
            // are sized to the curriculum's real rotation count from the start.
            if (basicData.student_ids.length > 0 || basicData.preceptor_ids.length > 0) {
                const usersBody = {};
                if (basicData.student_ids.length > 0) {
                    usersBody.student_ids = basicData.student_ids;
                }
                if (basicData.preceptor_ids.length > 0) {
                    usersBody.preceptor_ids = basicData.preceptor_ids;
                }

                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Adding users to curriculum:', usersBody);
                const addUsersResponse = await fetch(getApiUrl(`curriculum/${curriculumId}/add_users`), {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(usersBody)
                });

                if (!addUsersResponse.ok) {
                    const errorData = await addUsersResponse.text();
                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('Failed to add users to curriculum:', errorData);
                } else {
                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Users added to curriculum successfully');
                }
            }

            // Step 4: Set constraints (if any). The backend derives the entity type
            // (speciality vs institution) from the curriculum's rank.
            if (constraintsData.constraints.length > 0) {
                const constraintsPayload = {
                    constraints: constraintsData.constraints.map(constraint => ({
                        minimumNumber: constraint.minimumNumber,
                        entities: constraint.entities.map(e => e._id || e.id || e), // Extract IDs
                        fromRotation: constraint.fromRotation,
                        toRotation: constraint.toRotation
                    }))
                };

                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Setting constraints:', constraintsPayload);
                const constraintsResponse = await fetch(getApiUrl(`constraints/set_constraints_by_curriculum/${curriculumId}`), {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(constraintsPayload)
                });

                if (!constraintsResponse.ok) {
                    const errorData = await constraintsResponse.text();
                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn('Failed to set constraints:', errorData);
                } else {
                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Constraints set successfully');
                }
            }

            if (onNotifySuccess) {
                onNotifySuccess('Curriculum created successfully');
            } else if (!embedded) {
                alert('Curriculum created successfully!');
            }
            onSuccess?.(createdCurriculum);
            handleReset();
        } catch (error) {
            console.error('Error creating curriculum:', error);
            const errorMessage = `Error creating curriculum: ${error.message}`;
            if (onNotifyError) {
                onNotifyError(errorMessage);
            } else if (!embedded) {
                alert(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setCurrentStep(1);
        const defaults = getDefaultState();
        setBasicData(defaults.basicData);
        setConfigData(defaults.configData);
        setConstraintsData({ constraints: [] });
        if (embedded) {
            onCancel?.();
        } else {
            onClose?.();
        }
    };

    // Helper function for rank badge colors
    const getRankBadgeColor = (rank) => {
        const colors = {
            'pharmd': '#1e3a8a',
            'spep': '#7c2d12', 
            'both': '#059669'
        };
        return colors[rank?.toLowerCase()] || '#475569';
    };

    // Enhanced searchable multi-select component that stays open on selection
    const MultiSelectDropdown = ({ options, selectedIds, onChange, placeholder, loading, type, filterRankId, filterRankName, allRanks = [] }) => {
        const [isOpen, setIsOpen] = useState(false);
        const [searchTerm, setSearchTerm] = useState('');
        const dropdownRef = React.useRef(null);
        const searchInputRef = React.useRef(null);

        // Close dropdown when clicking outside, but not on selection
        React.useEffect(() => {
            const handleClickOutside = (event) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setIsOpen(false);
                    setSearchTerm(''); // Clear search when closing
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, []);

        // Auto-focus search input when dropdown opens
        React.useEffect(() => {
            if (isOpen && searchInputRef.current) {
                setTimeout(() => {
                    searchInputRef.current.focus();
                }, 100);
            }
        }, [isOpen]);

        // Restrict options to users eligible for the curriculum's rank.
        const rankFilteredOptions = (() => {
            if (type === 'student') {
                // Students carry a single rank name; unranked students stay visible
                // since they may still be eligible.
                if (!filterRankName) return options;
                return options.filter(option => {
                    const optionRank = option.rank?.toLowerCase();
                    if (!optionRank) return true;
                    return optionRank === filterRankName.toLowerCase();
                });
            }

            // Preceptors must list this rank in rankIds (legacy "both" counts for all).
            if (!filterRankId) return options;
            return options.filter(option => preceptorServesRank(option, filterRankId, allRanks));
        })();

        // Enhanced filtering with name, ID and email search
        const filteredOptions = rankFilteredOptions.filter(option => {
            const firstName = option.firstname || '';
            const lastName = option.lastname || '';
            const fullName = `${firstName} ${lastName}`.toLowerCase();
            const searchLower = searchTerm.toLowerCase();
            const id = (option._id || option.id || '').toString().toLowerCase();
            const email = (option.email || '').toLowerCase();
            return fullName.includes(searchLower) || 
                   id.includes(searchLower) ||
                   email.includes(searchLower);
        });

        // Show which rank the list is scoped to
        const ranksDisplayText = filterRankName ? ` (${filterRankName.toUpperCase()})` : '';

        // Get selected items data with full names
        const selectedItems = selectedIds.map(id => 
            options.find(option => (option._id || option.id) === id)
        ).filter(Boolean);

        const removeSelectedItem = (itemId, e) => {
            e.stopPropagation();
            const newSelectedIds = selectedIds.filter(id => id !== itemId);
            onChange(newSelectedIds);
        };

        const toggleSelectedItem = (itemId, e) => {
            e.stopPropagation();
            // Toggle selection but keep dropdown open
            if (selectedIds.includes(itemId)) {
                const newSelectedIds = selectedIds.filter(id => id !== itemId);
                onChange(newSelectedIds);
            } else {
                onChange([...selectedIds, itemId]);
            }
            // Keep dropdown open and maintain search focus
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        };

        // Select All functionality
        const handleSelectAll = (e) => {
            e.stopPropagation();
            if (selectedIds.length === filteredOptions.length) {
                // If all are selected, deselect all
                onChange([]);
            } else {
                // Select all filtered options
                const allFilteredIds = filteredOptions.map(option => option._id);
                onChange(allFilteredIds);
            }
            // Keep dropdown open and maintain search focus
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        };

        return (
            <div ref={dropdownRef} style={{ marginBottom: '2rem', position: 'relative' }}>
                <label style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.75rem', 
                    fontWeight: 600, 
                    color: '#374151',
                    fontSize: '1rem'
                }}>
                    {type === 'student' ? (
                        <><FaGraduationCap style={{ color: '#4a90e2' }} />Students{ranksDisplayText}</>
                    ) : (
                        <><FaUserTie style={{ color: '#4a90e2' }} />Preceptors{ranksDisplayText}</>
                    )}
                </label>

                {/* Selected Items Display */}
                {selectedItems.length > 0 && (
                    <div style={{
                        marginBottom: '0.75rem',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        padding: '0.75rem',
                        backgroundColor: '#f8fafc',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0'
                    }}>
                        {selectedItems.map(item => (
                            <div
                                key={item._id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    backgroundColor: 'white',
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '20px',
                                    border: '1px solid #d1d5db',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#374151',
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                                    <div style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: getRankBadgeColor(item.rank),
                                        flexShrink: 0
                                    }}></div>
                                    <span>{item.firstname} {item.lastname}</span>
                                    {item.rank && (
                                        <span style={{
                                            backgroundColor: getRankBadgeColor(item.rank),
                                            color: 'white',
                                            padding: '0.125rem 0.375rem',
                                            borderRadius: '10px',
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            textTransform: 'uppercase'
                                        }}>
                                            {item.rank}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={(e) => removeSelectedItem(item._id, e)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#9ca3af',
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        padding: '0.25rem',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s ease',
                                        width: '20px',
                                        height: '20px'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#fee2e2';
                                        e.target.style.color = '#dc2626';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.color = '#9ca3af';
                                    }}
                                    title="Remove"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Dropdown Trigger */}
                <div
                    onClick={() => setIsOpen(!isOpen)}
                    style={{
                        padding: '0.75rem 1rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        background: 'white',
                        cursor: 'pointer',
                        minHeight: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        fontSize: '0.875rem'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                    }}
                >
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {selectedIds.length > 0 && (
                            <div style={{
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                borderRadius: '12px',
                                padding: '0.125rem 0.5rem',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                minWidth: '1.25rem',
                                textAlign: 'center'
                            }}>
                                {selectedIds.length}
                            </div>
                        )}
                        <span style={{ 
                            color: selectedIds.length > 0 ? '#374151' : '#9ca3af',
                            fontSize: '0.875rem',
                            fontWeight: selectedIds.length > 0 ? 500 : 400
                        }}>
                            {selectedIds.length > 0 ? 
                                `${selectedIds.length} ${type}(s) selected` : 
                                placeholder
                            }
                        </span>
                    </div>
                    <div style={{ 
                        color: '#6b7280', 
                        fontSize: '0.75rem',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        ▼
                    </div>
                </div>

                {/* Dropdown Menu */}
                {isOpen && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        marginTop: '0.25rem',
                        maxHeight: '400px',
                        overflow: 'hidden',
                        zIndex: 9999,
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {/* Enhanced Search Header */}
                        <div style={{ 
                            padding: '0.75rem', 
                            borderBottom: '1px solid #f3f4f6', 
                            background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 100%)'
                        }}>
                            <div style={{ position: 'relative' }}>
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder={`🔍 Search ${type}s by name, ID, or email...`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1rem',
                                        border: '2px solid #e1e5e9',
                                        borderRadius: '12px',
                                        fontSize: '0.875rem',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        transition: 'all 0.2s ease',
                                        background: 'white',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#3b82f6';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 2px 8px rgba(0, 0, 0, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e1e5e9';
                                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                {searchTerm && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSearchTerm('');
                                            if (searchInputRef.current) {
                                                searchInputRef.current.focus();
                                            }
                                        }}
                                        style={{
                                            position: 'absolute',
                                            right: '0.75rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: '#9ca3af',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '20px',
                                            height: '20px',
                                            color: 'white',
                                            cursor: 'pointer',
                                            fontSize: '0.75rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.background = '#6b7280';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = '#9ca3af';
                                        }}
                                        title="Clear search"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            
                            {/* Select All Button and Search Results Info */}
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginTop: '0.75rem',
                                gap: '0.5rem'
                            }}>
                                {/* Select All Button */}
                                <button
                                    onClick={handleSelectAll}
                                    style={{
                                        background: selectedIds.length === filteredOptions.length && filteredOptions.length > 0
                                            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                            : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '0.5rem 0.75rem',
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.375rem',
                                        minWidth: 'fit-content'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                                    }}
                                    title={selectedIds.length === filteredOptions.length && filteredOptions.length > 0 
                                        ? `Deselect all ${type}s` 
                                        : `Select all ${type}s (${filteredOptions.length})`
                                    }
                                >
                                    {selectedIds.length === filteredOptions.length && filteredOptions.length > 0 ? (
                                        <>
                                            <FaTimes style={{ fontSize: '0.7rem' }} />
                                            Deselect All
                                        </>
                                    ) : (
                                        <>
                                            <FaCheckCircle style={{ fontSize: '0.7rem' }} />
                                            Select All ({filteredOptions.length})
                                        </>
                                    )}
                                </button>
                                
                                {/* Search Results Info */}
                                {searchTerm && (
                                    <div style={{ 
                                        fontSize: '0.75rem', 
                                        color: '#6b7280',
                                        fontWeight: 500,
                                        textAlign: 'right'
                                    }}>
                                        {filteredOptions.length} {type}(s) found
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Enhanced Options List */}
                        <div style={{ maxHeight: '280px', overflowY: 'auto', flexGrow: 1 }}>
                            {loading ? (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                                    <LoadingSpinner text={`Loading ${type}s...`} variant="inline" size="small" />
                                </div>
                            ) : filteredOptions.length === 0 ? (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>
                                        {searchTerm ? '🔍' : '📋'}
                                    </div>
                                    <div style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>
                                        {searchTerm ? `No ${type}s match "${searchTerm}"` : `No ${type}s available`}
                                    </div>
                                    {searchTerm && (
                                        <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                                            Try a different search term
                                        </div>
                                    )}
                                </div>
                            ) : (
                                filteredOptions.map(option => {
                                    const optionId = option._id || option.id;
                                    const currentIds = Array.isArray(selectedIds) ? selectedIds : [];
                                    const isSelected = currentIds.includes(optionId);
                                    return (
                                        <div
                                            key={optionId}
                                            onClick={(e) => toggleSelectedItem(optionId, e)}
                                            style={{
                                                padding: '0.875rem',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f1f5f9',
                                                backgroundColor: isSelected ? '#eff6ff' : 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                transition: 'all 0.2s ease',
                                                borderLeft: isSelected ? '4px solid #3b82f6' : '4px solid transparent'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.backgroundColor = '#f8fafc';
                                                    e.currentTarget.style.borderLeft = '4px solid #cbd5e1';
                                                } else {
                                                    e.currentTarget.style.backgroundColor = '#dbeafe';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.backgroundColor = 'white';
                                                    e.currentTarget.style.borderLeft = '4px solid transparent';
                                                } else {
                                                    e.currentTarget.style.backgroundColor = '#eff6ff';
                                                    e.currentTarget.style.borderLeft = '4px solid #3b82f6';
                                                }
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: '0.875rem' }}>
                                                <div style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '6px',
                                                    border: isSelected ? '2px solid #3b82f6' : '2px solid #cbd5e1',
                                                    backgroundColor: isSelected ? '#3b82f6' : 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    flexShrink: 0
                                                }}>
                                                    {isSelected ? '✓' : ''}
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ 
                                                        fontWeight: 600, 
                                                        color: isSelected ? '#1e40af' : '#111827',
                                                        fontSize: '0.875rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem'
                                                    }}>
                                                        {option.firstname} {option.lastname}
                                                        {option.rank && (
                                                            <span style={{
                                                                backgroundColor: getRankBadgeColor(option.rank),
                                                                color: 'white',
                                                                padding: '0.125rem 0.5rem',
                                                                borderRadius: '10px',
                                                                fontSize: '0.675rem',
                                                                fontWeight: 700,
                                                                textTransform: 'uppercase'
                                                            }}>
                                                                {option.rank}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ 
                                                        fontSize: '0.75rem', 
                                                        color: isSelected ? '#3b82f6' : '#6b7280',
                                                        marginTop: '0.125rem'
                                                    }}>
                                                        ID: {optionId} • {option.email || 'No email'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
    );
    };

    if (!embedded && !isOpen) return null;

    return (
        <div
            className={embedded ? 'mc-editor-panel mc-transition-up' : 'mc-modal-overlay mc-modal-overlay--strong mc-modal-overlay--top'}
            style={embedded ? { marginTop: '1.25rem', padding: 0 } : undefined}
        >
            <div className="mc-modal-surface mc-modal-surface--create" style={{
                background: 'white',
                borderRadius: '24px',
                maxWidth: embedded ? '100%' : '900px',
                maxHeight: embedded ? 'none' : '95vh',
                width: embedded ? '100%' : '90%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
                overflow: 'hidden'
            }}>
                {/* Header with dark blue gradient */}
                <div className="mc-modal-header mc-modal-header--create" style={{
                    background: 'linear-gradient(135deg, var(--mc-dark-header, #0f2740) 0%, var(--mc-dark-header-soft, #1a3a5c) 100%)',
                    padding: '2rem',
                    color: 'white',
                    position: 'relative'
                }}>
                    {!embedded && (
                        <button
                            className="mc-icon-close-btn"
                            onClick={handleReset}
                            title="Close"
                            style={{
                                position: 'absolute',
                                top: '1.5rem',
                                right: '1.5rem',
                                background: 'none',
                                border: 'none',
                                color: 'rgba(255, 255, 255, 0.9)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.8rem',
                                fontWeight: 'bold',
                                transition: 'transform 0.2s ease',
                                zIndex: 10,
                                padding: '0.5rem',
                                borderRadius: '4px'
                            }}
                        >
                            <FaTimes />
                        </button>
                    )}
                    
                    <div style={{ marginBottom: '1.5rem' }}>
                        <h2 style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: 'white' }}>
                            Create New Curriculum
                        </h2>
                        <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '1.1rem' }}>
                            Set up a comprehensive curriculum with customizable rotations and constraints
                        </p>
                    </div>

                    {/* Progress indicator */}
                    <div style={{ display: 'grid', gap: '0.9rem' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '0.86rem',
                            fontWeight: 600
                        }}>
                            <span>Stage {currentStep} of 4</span>
                            <span>{stageMeta.find((s) => s.id === currentStep)?.title}</span>
                        </div>
                        <div style={{ height: '8px', borderRadius: '999px', background: 'rgba(255, 255, 255, 0.26)', overflow: 'hidden' }}>
                            <div style={{
                                height: '100%',
                                width: `${(currentStep / 4) * 100}%`,
                                background: 'linear-gradient(90deg, #f8fbff 0%, #dbeafe 100%)',
                                transition: 'width 0.25s ease'
                            }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        {stageMeta.map((step, index) => (
                            <React.Fragment key={step.id}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    padding: '0.75rem 1rem',
                                    borderRadius: '12px',
                                    background: currentStep >= step.id
                                        ? 'rgba(255, 255, 255, 0.2)' 
                                        : 'rgba(255, 255, 255, 0.1)',
                                    border: currentStep === step.id
                                        ? '2px solid rgba(255, 255, 255, 0.5)' 
                                        : '2px solid transparent',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div style={{
                                        width: '2rem',
                                        height: '2rem',
                                        borderRadius: '50%',
                                        background: currentStep >= step.id ? 'white' : 'rgba(255, 255, 255, 0.3)',
                                        color: currentStep >= step.id ? '#2b4663' : 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 700,
                                        fontSize: '0.9rem'
                                    }}>
                                        {step.id}
                                    </div>
                                    <span style={{ 
                                        fontWeight: 600, 
                                        fontSize: '0.9rem',
                                        opacity: currentStep >= step.id ? 1 : 0.7
                                    }}>
                                        {step.title}
                                    </span>
                                </div>
                                {index < 3 && (
                                    <FaArrowRight style={{ opacity: 0.5, fontSize: '0.8rem' }} />
                                )}
                            </React.Fragment>
                        ))}
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div style={{
                    flex: 1,
                    padding: '2rem',
                    overflowY: 'auto',
                    background: 'linear-gradient(180deg, #f8fafc 0%, #eef4fb 100%)'
                }}>
                    {/* Step 1: Basic Information */}
                    {currentStep === 1 && (
                        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                            <div style={{ marginBottom: '2rem' }}>
                                <h3 style={{ 
                                    fontSize: '1.5rem', 
                                    fontWeight: 600, 
                                    color: '#1f2937',
                                    marginBottom: '0.5rem'
                                }}>
                                    Basic Information
                                </h3>
                                <p style={{ color: '#6b7280', fontSize: '1rem', margin: 0 }}>
                                    Provide the essential details for your new curriculum
                                </p>
                            </div>

                            <div style={{ display: 'grid', gap: '1.5rem' }}>
                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '0.5rem', 
                                        fontWeight: 600, 
                                        color: '#374151'
                                    }}>
                                        <FaBook style={{ marginRight: '0.5rem', color: '#4a90e2' }} />
                                        Curriculum Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={basicData.name}
                                        onChange={(e) => setBasicData({...basicData, name: e.target.value})}
                                        placeholder="Enter curriculum name..."
                                        style={{
                                            width: '100%',
                                            padding: '0.875rem',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '12px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            background: 'white',
                                            boxSizing: 'border-box',
                                            transition: 'border-color 0.2s ease'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                    />
                                </div>

                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '0.5rem', 
                                        fontWeight: 600, 
                                        color: '#374151'
                                    }}>
                                        <FaClipboardList style={{ marginRight: '0.5rem', color: '#4a90e2' }} />
                                        Description
                                    </label>
                                    <textarea
                                        value={basicData.description}
                                        onChange={(e) => setBasicData({...basicData, description: e.target.value})}
                                        placeholder="Describe the curriculum objectives and structure..."
                                        rows={4}
                                        style={{
                                            width: '100%',
                                            padding: '0.875rem',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '12px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            background: 'white',
                                            boxSizing: 'border-box',
                                            resize: 'vertical',
                                            transition: 'border-color 0.2s ease'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <label style={{ 
                                            display: 'block', 
                                            marginBottom: '0.5rem', 
                                            fontWeight: 600, 
                                            color: '#374151'
                                        }}>
                                            <FaCalendarAlt style={{ marginRight: '0.5rem', color: '#4a90e2' }} />
                                            Start Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={basicData.start_date}
                                            onChange={(e) => setBasicData({...basicData, start_date: e.target.value})}
                                            style={{
                                                width: '100%',
                                                padding: '0.875rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '12px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                background: 'white',
                                                boxSizing: 'border-box',
                                                transition: 'border-color 0.2s ease'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ 
                                            display: 'block', 
                                            marginBottom: '0.5rem', 
                                            fontWeight: 600, 
                                            color: '#374151'
                                        }}>
                                            <FaCalendarAlt style={{ marginRight: '0.5rem', color: '#4a90e2' }} />
                                            End Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={basicData.end_date}
                                            onChange={(e) => setBasicData({...basicData, end_date: e.target.value})}
                                            style={{
                                                width: '100%',
                                                padding: '0.875rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '12px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                background: 'white',
                                                boxSizing: 'border-box',
                                                transition: 'border-color 0.2s ease'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#4a90e2'}
                                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '0.75rem', 
                                        fontWeight: 600, 
                                        color: '#374151'
                                    }}>
                                        <FaGraduationCap style={{ marginRight: '0.5rem', color: '#4a90e2' }} />
                                        Rank *
                                    </label>
                                    <div style={{
                                        padding: '1rem',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '12px',
                                        background: 'white',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.75rem'
                                    }}>
                                        {ranksLoading ? (
                                            <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>Loading ranks…</span>
                                        ) : ranks.length === 0 ? (
                                            <div style={{ color: '#ef4444', fontSize: '0.875rem', fontStyle: 'italic' }}>
                                                No ranks exist yet. Create one from Manage Ranks first.
                                            </div>
                                        ) : (
                                            <>
                                                <select
                                                    value={basicData.rankId}
                                                    onChange={(e) => handleRankChange(e.target.value)}
                                                    style={{
                                                        padding: '0.7rem',
                                                        border: '1px solid #d7e1ec',
                                                        borderRadius: '8px',
                                                        fontSize: '0.95rem',
                                                        color: '#163857',
                                                        background: 'white'
                                                    }}
                                                >
                                                    <option value="">Select a rank…</option>
                                                    {ranks.map((rank) => (
                                                        <option key={rank._id} value={rank._id}>
                                                            {rank.name} — matched by {rank.matchBy}
                                                        </option>
                                                    ))}
                                                </select>

                                                {selectedRank ? (
                                                    <div style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: '0.5rem',
                                                        alignItems: 'center',
                                                        fontSize: '0.85rem',
                                                        color: '#475569'
                                                    }}>
                                                        <span style={{
                                                            background: getRankBadgeColor(selectedRank.name),
                                                            color: 'white',
                                                            padding: '0.25rem 0.75rem',
                                                            borderRadius: '12px',
                                                            fontSize: '0.8rem',
                                                            fontWeight: 600,
                                                            textTransform: 'uppercase'
                                                        }}>
                                                            {selectedRank.name}
                                                        </span>
                                                        <span>
                                                            Matched by {matchByMeta.label.toLowerCase()} • defaults to{' '}
                                                            {selectedRank.defaultRotationsCount} rotations
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div style={{ color: '#ef4444', fontSize: '0.875rem', fontStyle: 'italic' }}>
                                                        Please select a rank
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                <MultiSelectDropdown
                                    options={students}
                                    selectedIds={basicData.student_ids}
                                    onChange={(selectedIds) => setBasicData({...basicData, student_ids: selectedIds})}
                                    placeholder="Select students..."
                                    loading={studentsLoading}
                                    type="student"
                                    filterRankName={selectedRank?.name}
                                />

                                <MultiSelectDropdown
                                    options={preceptors}
                                    selectedIds={basicData.preceptor_ids}
                                    onChange={(selectedIds) => setBasicData({...basicData, preceptor_ids: selectedIds})}
                                    placeholder="Select preceptors..."
                                    loading={preceptorsLoading}
                                    type="preceptor"
                                    filterRankId={basicData.rankId}
                                    filterRankName={selectedRank?.name}
                                    allRanks={ranks}
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 4: Form Management */}
                    {currentStep === 4 && (
                        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                            <div style={{ marginBottom: '2rem' }}>
                                <h3 style={{ 
                                    fontSize: '1.5rem', 
                                    fontWeight: 600, 
                                    color: '#1f2937',
                                    marginBottom: '0.5rem'
                                }}>
                                    Form Management
                                </h3>
                                <p style={{ color: '#6b7280', fontSize: '1rem', margin: 0 }}>
                                    Configure forms for your curriculum with specific schedules (Optional - can be set up later)
                                </p>
                            </div>

                            <CurriculumFormManager
                                curriculumForms={formsData}
                                onFormsChange={setFormsData}
                                canSkip={true}
                                isExpanded={true}
                            />
                        </div>
                    )}

                    {/* Step 2: Configuration - Simple and Clean */}
                    {currentStep === 2 && (
                        <div style={{ 
                            maxWidth: '800px',
                            margin: '0 auto',
                            padding: '2rem',
                            background: 'white',
                            borderRadius: '16px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                        }}>
                            {/* Simple header */}
                            <div style={{ marginBottom: '2rem' }}>
                                <h3 style={{ 
                                    fontSize: '1.5rem', 
                                    fontWeight: 600, 
                                    color: '#1f2937',
                                    marginBottom: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem'
                                }}>
                                    <FaCog style={{ color: '#3b82f6' }} />
                                    Configuration Settings
                                </h3>
                                <p style={{ 
                                    color: '#6b7280', 
                                    fontSize: '1rem',
                                    margin: 0,
                                    lineHeight: 1.5
                                }}>
                                    Set up rotation schedules and system preferences for your curriculum
                                </p>
                            </div>

                            {/* Form Grid */}
                            <div style={{ display: 'grid', gap: '2rem' }}>
                                {/* Basic Settings */}
                                <div style={{
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    padding: '1.5rem',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <h4 style={{ 
                                        fontSize: '1.1rem', 
                                        fontWeight: 600, 
                                        color: '#374151',
                                        marginBottom: '1.25rem',
                                        margin: 0
                                    }}>
                                        Basic Settings
                                    </h4>
                                    
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                                        gap: '1.5rem',
                                        marginTop: '1.25rem'
                                    }}>
                                        <div>
                                            <label style={{ 
                                                display: 'block', 
                                                marginBottom: '0.5rem', 
                                                fontWeight: 500, 
                                                color: '#374151',
                                                fontSize: '0.9rem'
                                            }}>
                                                Number of Rotations
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="20"
                                                value={configData.rotationsCount}
                                                onChange={(e) => setConfigData({...configData, rotationsCount: parseInt(e.target.value) || 1})}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    fontSize: '0.9rem',
                                                    outline: 'none',
                                                    background: 'white',
                                                    boxSizing: 'border-box',
                                                    transition: 'border-color 0.2s ease'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                            />
                                        </div>
                                        
                                        <div>
                                            <label style={{ 
                                                display: 'block', 
                                                marginBottom: '0.5rem', 
                                                fontWeight: 500, 
                                                color: '#374151',
                                                fontSize: '0.9rem'
                                            }}>
                                                Student Choices Count
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="10"
                                                value={configData.choicesCount}
                                                onChange={(e) => setConfigData({...configData, choicesCount: parseInt(e.target.value) || 1})}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    fontSize: '0.9rem',
                                                    outline: 'none',
                                                    background: 'white',
                                                    boxSizing: 'border-box',
                                                    transition: 'border-color 0.2s ease'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Registration Period */}
                                <div style={{
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    padding: '1.5rem',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <h4 style={{ 
                                        fontSize: '1.1rem', 
                                        fontWeight: 600, 
                                        color: '#374151',
                                        marginBottom: '1.25rem',
                                        margin: 0
                                    }}>
                                        Form Registration Period
                                    </h4>
                                    
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                                        gap: '1.5rem',
                                        marginTop: '1.25rem'
                                    }}>
                                        <div>
                                            <label style={{ 
                                                display: 'block', 
                                                marginBottom: '0.5rem', 
                                                fontWeight: 500, 
                                                color: '#374151',
                                                fontSize: '0.9rem'
                                            }}>
                                                Start Date
                                            </label>
                                            <input
                                                type="date"
                                                value={configData.form_registration?.startDate || ''}
                                                onChange={(e) => setConfigData({
                                                    ...configData,
                                                    form_registration: {
                                                        ...configData.form_registration,
                                                        startDate: e.target.value
                                                    }
                                                })}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    fontSize: '0.9rem',
                                                    outline: 'none',
                                                    background: 'white',
                                                    boxSizing: 'border-box',
                                                    transition: 'border-color 0.2s ease'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                            />
                                        </div>
                                        
                                        <div>
                                            <label style={{ 
                                                display: 'block', 
                                                marginBottom: '0.5rem', 
                                                fontWeight: 500, 
                                                color: '#374151',
                                                fontSize: '0.9rem'
                                            }}>
                                                End Date
                                            </label>
                                            <input
                                                type="date"
                                                value={configData.form_registration?.endDate || ''}
                                                onChange={(e) => setConfigData({
                                                    ...configData,
                                                    form_registration: {
                                                        ...configData.form_registration,
                                                        endDate: e.target.value
                                                    }
                                                })}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    fontSize: '0.9rem',
                                                    outline: 'none',
                                                    background: 'white',
                                                    boxSizing: 'border-box',
                                                    transition: 'border-color 0.2s ease'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Working Hours */}
                                <div style={{
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    padding: '1.5rem',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <h4 style={{ 
                                        fontSize: '1.1rem', 
                                        fontWeight: 600, 
                                        color: '#374151',
                                        marginBottom: '1.25rem',
                                        margin: 0
                                    }}>
                                        Default Working Hours
                                    </h4>
                                    
                                    <div style={{ 
                                        display: 'grid', 
                                        gap: '1.5rem',
                                        marginTop: '1.25rem'
                                    }}>
                                        <div style={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                                            gap: '1rem'
                                        }}>
                                            <div>
                                                <label style={{ 
                                                    display: 'block', 
                                                    marginBottom: '0.5rem', 
                                                    fontWeight: 500, 
                                                    color: '#374151',
                                                    fontSize: '0.9rem'
                                                }}>
                                                    Full-Time Start
                                                </label>
                                                <input
                                                    type="time"
                                                    value={configData.defaultFullTimeHrs.start_time}
                                                    onChange={(e) => setConfigData({
                                                        ...configData, 
                                                        defaultFullTimeHrs: {
                                                            ...configData.defaultFullTimeHrs,
                                                            start_time: e.target.value
                                                        }
                                                    })}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.75rem',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '8px',
                                                        fontSize: '0.9rem',
                                                        outline: 'none',
                                                        background: 'white',
                                                        boxSizing: 'border-box',
                                                        transition: 'border-color 0.2s ease'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                                />
                                            </div>
                                            
                                            <div>
                                                <label style={{ 
                                                    display: 'block', 
                                                    marginBottom: '0.5rem', 
                                                    fontWeight: 500, 
                                                    color: '#374151',
                                                    fontSize: '0.9rem'
                                                }}>
                                                    Full-Time End
                                                </label>
                                                <input
                                                    type="time"
                                                    value={configData.defaultFullTimeHrs.end_time}
                                                    onChange={(e) => setConfigData({
                                                        ...configData, 
                                                        defaultFullTimeHrs: {
                                                            ...configData.defaultFullTimeHrs,
                                                            end_time: e.target.value
                                                        }
                                                    })}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.75rem',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '8px',
                                                        fontSize: '0.9rem',
                                                        outline: 'none',
                                                        background: 'white',
                                                        boxSizing: 'border-box',
                                                        transition: 'border-color 0.2s ease'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                                />
                                            </div>
                                        </div>
                                        
                                        <div style={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                                            gap: '1rem'
                                        }}>
                                            <div>
                                                <label style={{ 
                                                    display: 'block', 
                                                    marginBottom: '0.5rem', 
                                                    fontWeight: 500, 
                                                    color: '#374151',
                                                    fontSize: '0.9rem'
                                                }}>
                                                    Part-Time Start
                                                </label>
                                                <input
                                                    type="time"
                                                    value={configData.defaultPartTimeHrs.start_time}
                                                    onChange={(e) => setConfigData({
                                                        ...configData, 
                                                        defaultPartTimeHrs: {
                                                            ...configData.defaultPartTimeHrs,
                                                            start_time: e.target.value
                                                        }
                                                    })}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.75rem',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '8px',
                                                        fontSize: '0.9rem',
                                                        outline: 'none',
                                                        background: 'white',
                                                        boxSizing: 'border-box',
                                                        transition: 'border-color 0.2s ease'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                                />
                                            </div>
                                            
                                            <div>
                                                <label style={{ 
                                                    display: 'block', 
                                                    marginBottom: '0.5rem', 
                                                    fontWeight: 500, 
                                                    color: '#374151',
                                                    fontSize: '0.9rem'
                                                }}>
                                                    Part-Time End
                                                </label>
                                                <input
                                                    type="time"
                                                    value={configData.defaultPartTimeHrs.end_time}
                                                    onChange={(e) => setConfigData({
                                                        ...configData, 
                                                        defaultPartTimeHrs: {
                                                            ...configData.defaultPartTimeHrs,
                                                            end_time: e.target.value
                                                        }
                                                    })}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.75rem',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '8px',
                                                        fontSize: '0.9rem',
                                                        outline: 'none',
                                                        background: 'white',
                                                        boxSizing: 'border-box',
                                                        transition: 'border-color 0.2s ease'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Rotation Schedule */}
                                <div style={{
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    padding: '1.5rem',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <h4 style={{ 
                                        fontSize: '1.1rem', 
                                        fontWeight: 600, 
                                        color: '#374151',
                                        marginBottom: '1.25rem',
                                        margin: 0
                                    }}>
                                        Rotation Schedule ({configData.rotationsCount} rotations)
                                    </h4>
                                    
                                    <div style={{ 
                                        display: 'grid', 
                                        gap: '1.5rem',
                                        marginTop: '1.25rem'
                                    }}>
                                        {configData.rotations.map((rotation, index) => (
                                            <div key={index} style={{
                                                background: 'white',
                                                borderRadius: '8px',
                                                padding: '1.25rem',
                                                border: '1px solid #e2e8f0'
                                            }}>
                                                <h5 style={{ 
                                                    margin: '0 0 1rem 0', 
                                                    fontSize: '1rem', 
                                                    fontWeight: 600,
                                                    color: '#374151'
                                                }}>
                                                    Rotation {index + 1}
                                                </h5>
                                                <div style={{ 
                                                    display: 'grid', 
                                                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                                                    gap: '1rem' 
                                                }}>
                                                    <div>
                                                        <label style={{ 
                                                            display: 'block', 
                                                            marginBottom: '0.5rem', 
                                                            fontWeight: 500, 
                                                            fontSize: '0.9rem',
                                                            color: '#374151'
                                                        }}>
                                                            Start Date
                                                        </label>
                                                        <input
                                                            type="date"
                                                            value={rotation.startDate}
                                                            onChange={(e) => {
                                                                const newRotations = [...configData.rotations];
                                                                newRotations[index] = {
                                                                    ...newRotations[index],
                                                                    startDate: e.target.value
                                                                };
                                                                setConfigData({...configData, rotations: newRotations});
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '0.75rem',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                fontSize: '0.9rem',
                                                                outline: 'none',
                                                                background: 'white',
                                                                boxSizing: 'border-box',
                                                                transition: 'border-color 0.2s ease'
                                                            }}
                                                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label style={{ 
                                                            display: 'block', 
                                                            marginBottom: '0.5rem', 
                                                            fontWeight: 500, 
                                                            fontSize: '0.9rem',
                                                            color: '#374151'
                                                        }}>
                                                            End Date
                                                        </label>
                                                        <input
                                                            type="date"
                                                            value={rotation.endDate}
                                                            onChange={(e) => {
                                                                const newRotations = [...configData.rotations];
                                                                newRotations[index] = {
                                                                    ...newRotations[index],
                                                                    endDate: e.target.value
                                                                };
                                                                setConfigData({...configData, rotations: newRotations});
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '0.75rem',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                fontSize: '0.9rem',
                                                                outline: 'none',
                                                                background: 'white',
                                                                boxSizing: 'border-box',
                                                                transition: 'border-color 0.2s ease'
                                                            }}
                                                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Preferences */}
                                <div style={{
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    padding: '1.5rem',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <h4 style={{ 
                                        fontSize: '1.1rem', 
                                        fontWeight: 600, 
                                        color: '#374151',
                                        marginBottom: '1.25rem',
                                        margin: 0
                                    }}>
                                        Preferences
                                    </h4>
                                    
                                    <div style={{ 
                                        display: 'grid', 
                                        gap: '1rem',
                                        marginTop: '1.25rem'
                                    }}>
                                        <label style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                            fontWeight: 500,
                                            color: '#374151'
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={configData.spep_prefs}
                                                onChange={(e) => setConfigData({...configData, spep_prefs: e.target.checked})}
                                                style={{ 
                                                    width: '16px', 
                                                    height: '16px',
                                                    accentColor: '#3b82f6'
                                                }}
                                            />
                                            Enable SPEP Preferences
                                        </label>
                                        
                                        <label style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                            fontWeight: 500,
                                            color: '#374151'
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={configData.sitePreceptorOnly}
                                                onChange={(e) => setConfigData({...configData, sitePreceptorOnly: e.target.checked})}
                                                style={{ 
                                                    width: '16px', 
                                                    height: '16px',
                                                    accentColor: '#3b82f6'
                                                }}
                                            />
                                            Site Preceptor Only
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Constraints */}
                    {currentStep === 3 && (
                        <div style={{
                            maxWidth: '800px',
                            margin: '0 auto',
                            padding: '2rem',
                            background: 'white',
                            borderRadius: '16px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                        }}>
                            <div style={{ marginBottom: '2rem' }}>
                                <h3 style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 600,
                                    color: '#1f2937',
                                    marginBottom: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem'
                                }}>
                                    <FaCog style={{ color: '#3b82f6' }} />
                                    Curriculum Constraints
                                </h3>
                                <p style={{
                                    color: '#6b7280',
                                    fontSize: '1rem',
                                    margin: 0,
                                    lineHeight: 1.5
                                }}>
                                    {selectedRank
                                        ? `Define minimum requirements for ${selectedRank.name} students. This rank is matched by ${matchByMeta.label.toLowerCase()}, so constraints target ${matchByMeta.pluralLabel.toLowerCase()}.`
                                        : 'Select a rank in the first step to configure constraints.'}
                                </p>
                            </div>

                            {!selectedRank ? (
                                <div style={{
                                    background: '#f8fafc',
                                    borderRadius: '8px',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    border: '2px dashed #e2e8f0'
                                }}>
                                    <FaInfoCircle style={{ fontSize: '2rem', color: '#6b7280', marginBottom: '0.75rem' }} />
                                    <h5 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', margin: '0 0 0.5rem 0' }}>
                                        No Rank Selected
                                    </h5>
                                    <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>
                                        Constraints depend on the rank's match type — go back to Basic Info and pick a rank.
                                    </p>
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gap: '2rem' }}>
                                    <div style={{
                                        background: '#f8fafc',
                                        borderRadius: '12px',
                                        padding: '1.5rem',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginBottom: '1.25rem'
                                        }}>
                                            <div>
                                                <h4 style={{
                                                    fontSize: '1.1rem',
                                                    fontWeight: 600,
                                                    color: '#374151',
                                                    margin: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}>
                                                    <FaGraduationCap style={{ color: '#3b82f6' }} />
                                                    {selectedRank.name} Constraints
                                                </h4>
                                                <p style={{
                                                    color: '#6b7280',
                                                    fontSize: '0.9rem',
                                                    margin: '0.25rem 0 0 0'
                                                }}>
                                                    Set minimum {matchByMeta.pluralLabel.toLowerCase()} required across rotations
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setConstraintsData({
                                                        constraints: [...constraintsData.constraints, createNewConstraint()]
                                                    });
                                                }}
                                                style={{
                                                    background: '#3b82f6',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    padding: '0.75rem 1rem',
                                                    color: 'white',
                                                    fontSize: '0.9rem',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    transition: 'background 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => { e.target.style.background = '#2563eb'; }}
                                                onMouseLeave={(e) => { e.target.style.background = '#3b82f6'; }}
                                            >
                                                <FaPlus />
                                                Add Constraint
                                            </button>
                                        </div>

                                        <div style={{ display: 'grid', gap: '1.5rem' }}>
                                            {constraintsData.constraints.map((constraint, index) => (
                                                <div key={constraint.id || index} style={{
                                                    background: 'white',
                                                    borderRadius: '8px',
                                                    padding: '1.25rem',
                                                    border: '1px solid #e2e8f0',
                                                    position: 'relative'
                                                }}>
                                                    <button
                                                        onClick={() => {
                                                            setConstraintsData({
                                                                constraints: constraintsData.constraints.filter((_, i) => i !== index)
                                                            });
                                                        }}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '1rem',
                                                            right: '1rem',
                                                            background: '#ef4444',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            padding: '0.5rem',
                                                            color: 'white',
                                                            cursor: 'pointer',
                                                            fontSize: '0.875rem',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.background = '#dc2626';
                                                            e.target.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.background = '#ef4444';
                                                            e.target.style.transform = 'scale(1)';
                                                        }}
                                                    >
                                                        <FaMinus />
                                                    </button>

                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                                        gap: '1.5rem',
                                                        marginTop: '0.5rem'
                                                    }}>
                                                        <div>
                                                            <label style={{
                                                                display: 'block',
                                                                marginBottom: '0.75rem',
                                                                fontWeight: 600,
                                                                color: '#374151',
                                                                fontSize: '0.9rem'
                                                            }}>
                                                                Minimum Number
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={constraint.minimumNumber}
                                                                onChange={(e) => updateConstraint(index, 'minimumNumber', parseInt(e.target.value) || 1)}
                                                                style={constraintFieldStyle}
                                                                onFocus={handleConstraintFieldFocus}
                                                                onBlur={handleConstraintFieldBlur}
                                                            />
                                                        </div>

                                                        <div>
                                                            <label style={{
                                                                display: 'block',
                                                                marginBottom: '0.75rem',
                                                                fontWeight: 600,
                                                                color: '#374151',
                                                                fontSize: '0.9rem'
                                                            }}>
                                                                From Rotation
                                                            </label>
                                                            <select
                                                                value={constraint.fromRotation}
                                                                onChange={(e) => updateConstraint(index, 'fromRotation', parseInt(e.target.value))}
                                                                style={constraintFieldStyle}
                                                                onFocus={handleConstraintFieldFocus}
                                                                onBlur={handleConstraintFieldBlur}
                                                            >
                                                                {Array.from({ length: configData.rotationsCount }, (_, i) => (
                                                                    <option key={i + 1} value={i + 1}>
                                                                        Rotation {i + 1}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div>
                                                            <label style={{
                                                                display: 'block',
                                                                marginBottom: '0.75rem',
                                                                fontWeight: 600,
                                                                color: '#374151',
                                                                fontSize: '0.9rem'
                                                            }}>
                                                                To Rotation
                                                            </label>
                                                            <select
                                                                value={constraint.toRotation}
                                                                onChange={(e) => updateConstraint(index, 'toRotation', parseInt(e.target.value))}
                                                                style={constraintFieldStyle}
                                                                onFocus={handleConstraintFieldFocus}
                                                                onBlur={handleConstraintFieldBlur}
                                                            >
                                                                {Array.from({ length: configData.rotationsCount }, (_, i) => (
                                                                    <option key={i + 1} value={i + 1}>
                                                                        Rotation {i + 1}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div style={{ marginTop: '1.5rem' }}>
                                                        <ModernMultiSelectDropdown
                                                            items={constraintEntities}
                                                            selectedItems={constraint.entities}
                                                            onSelectionChange={(selectedItems) => updateConstraint(index, 'entities', selectedItems)}
                                                            placeholder={`Select ${matchByMeta.pluralLabel.toLowerCase()}...`}
                                                            loading={matchBy === MATCH_BY_INSTITUTION ? institutionsLoading : specialitiesLoading}
                                                            label={matchByMeta.pluralLabel}
                                                        />
                                                    </div>
                                                </div>
                                            ))}

                                            {constraintsData.constraints.length === 0 && (
                                                <div style={{
                                                    background: '#f8fafc',
                                                    borderRadius: '8px',
                                                    padding: '1.5rem',
                                                    textAlign: 'center',
                                                    border: '2px dashed #e2e8f0'
                                                }}>
                                                    <FaInfoCircle style={{
                                                        fontSize: '2rem',
                                                        color: '#6b7280',
                                                        marginBottom: '0.75rem'
                                                    }} />
                                                    <h5 style={{
                                                        fontSize: '1rem',
                                                        fontWeight: 600,
                                                        color: '#374151',
                                                        margin: '0 0 0.5rem 0'
                                                    }}>
                                                        No Constraints Yet
                                                    </h5>
                                                    <p style={{
                                                        color: '#6b7280',
                                                        fontSize: '0.9rem',
                                                        margin: 0,
                                                        lineHeight: 1.5
                                                    }}>
                                                        Click "Add Constraint" to define minimum requirements for {selectedRank.name} students
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Summary Section */}
                            {constraintsData.constraints.length > 0 && (
                                <div style={{
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    padding: '1.5rem',
                                    border: '1px solid #e2e8f0',
                                    marginTop: '1rem'
                                }}>
                                    <h4 style={{
                                        fontSize: '1.1rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '1.25rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <FaCheckCircle style={{ color: '#3b82f6' }} />
                                        Constraints Summary
                                    </h4>
                                    <h5 style={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: '#1e3a8a',
                                        margin: '0 0 0.75rem 0'
                                    }}>
                                        {selectedRank?.name} Constraints: {constraintsData.constraints.length}
                                    </h5>
                                    {constraintsData.constraints.map((constraint, index) => (
                                        <div key={index} style={{
                                            background: 'white',
                                            padding: '0.875rem',
                                            borderRadius: '10px',
                                            fontSize: '0.85rem',
                                            color: '#374151',
                                            marginBottom: '0.5rem',
                                            border: '1px solid #e5e7eb'
                                        }}>
                                            Min {constraint.minimumNumber} from rotations {constraint.fromRotation}-{constraint.toRotation}
                                            {constraint.entities.length > 0 && (
                                                <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#6b7280' }}>
                                                    {matchByMeta.pluralLabel}: {constraint.entities.map(e => typeof e === 'string' ? e : (e.name || e.speciality)).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer with Navigation */}
                <div style={{
                    background: '#f8fafc',
                    borderTop: '1px solid #e5e7eb',
                    padding: '1.5rem 2rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                            onClick={handleReset}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.75rem 1.5rem',
                                background: 'white',
                                border: '2px solid #ef4444',
                                borderRadius: '12px',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                color: '#ef4444',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#ef4444';
                                e.target.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'white';
                                e.target.style.color = '#ef4444';
                            }}
                        >
                            <FaTimes />
                            Cancel
                        </button>
                        {currentStep > 1 && (
                            <button
                                onClick={handlePrevious}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.75rem 1.5rem',
                                    background: 'white',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '12px',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    color: '#374151',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.borderColor = '#2b4663';
                                    e.target.style.color = '#2b4663';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.borderColor = '#e5e7eb';
                                    e.target.style.color = '#374151';
                                }}
                            >
                                <FaArrowLeft />
                                Previous
                            </button>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '1rem' }}>
                        {currentStep < 4 ? (
                            <button
                                onClick={handleNext}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.75rem 1.5rem',
                                    background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                                    border: 'none',
                                    borderRadius: '12px',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    color: 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(43, 70, 99, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                Next
                                <FaArrowRight />
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.75rem 1.5rem',
                                    background: loading 
                                        ? '#9ca3af' 
                                        : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                    border: 'none',
                                    borderRadius: '12px',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    color: 'white',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.3)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!loading) {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = 'none';
                                    }
                                }}
                            >
                                {loading ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaSave />}
                                {loading ? 'Creating...' : 'Create Curriculum'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateCurriculumModern;
